import { useState, useCallback } from "react";

// Helper function to create HMAC-SHA256 signature
async function createHmacSignature(
  queryString: string,
  apiSecret: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const key = encoder.encode(apiSecret);
  const message = encoder.encode(queryString);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  const signature = await crypto.subtle.sign("HMAC", cryptoKey, message);
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// --- Binance server time sync ---
let binanceTimeOffset = 0;

async function syncBinanceTime(baseUrl: string) {
  try {
    const timeUrl = baseUrl.includes("fapi")
      ? `${baseUrl}/fapi/v1/time`
      : `${baseUrl}/api/v3/time`;
    const res = await fetch(timeUrl);
    const data = await res.json();
    const serverTime = data.serverTime;
    const localTime = Date.now();
    binanceTimeOffset = serverTime - localTime;
  } catch (e) {
    console.error("Failed to sync Binance time", e);
  }
}

function getBinanceTimestamp() {
  return Date.now() + binanceTimeOffset - 200;
}

interface RebateValidationResult {
  ifNewUser: boolean;
  rebateWorking: boolean;
  isQualified: boolean;
}

interface UseBinanceRebateValidatorResult {
  isLoading: boolean;
  error: string | null;
  validationResult: RebateValidationResult | null;
  validateKeys: (
    apiKey: string,
    apiSecret: string,
    exchangeType: "SPOT" | "PERPETUAL",
    testnet: boolean,
  ) => Promise<RebateValidationResult | null>;
  resetValidator: () => void;
}

// Use different agent codes for SPOT and PERPETUAL
const API_AGENT_CODE_SPOT = "KUW8WW8A";
const API_AGENT_CODE_PERPETUAL = "YkA7cteB";

export function useBinanceRebateValidator(): UseBinanceRebateValidatorResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] =
    useState<RebateValidationResult | null>(null);

  const resetValidator = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setValidationResult(null);
  }, []);

  const validateKeys = useCallback(
    async (
      apiKey: string,
      apiSecret: string,
      exchangeType: "SPOT" | "PERPETUAL",
      testnet: boolean,
    ): Promise<RebateValidationResult | null> => {
      setIsLoading(true);
      setError(null);
      setValidationResult(null);

      if (!apiKey || !apiSecret) {
        setError("API Key and Secret are required.");
        setIsLoading(false);
        return null;
      }

      let baseUrl: string;
      let path: string;
      let apiAgentCode = "";

      if (exchangeType === "PERPETUAL") {
        baseUrl = testnet
          ? "https://testnet.binancefuture.com"
          : "https://fapi.binance.com";
        path = "/fapi/v1/apiReferral/ifNewUser";
        apiAgentCode = API_AGENT_CODE_PERPETUAL;
      } else {
        // Spot endpoint does not have a documented testnet equivalent for this
        if (testnet) {
          setError("Rebate check is not available for SPOT testnet.");
          setIsLoading(false);
          return null;
        }
        baseUrl = "https://api.binance.com";
        path = "/sapi/v1/apiReferral/ifNewUser";
        apiAgentCode = API_AGENT_CODE_SPOT;
      }

      try {
        await syncBinanceTime(baseUrl);
        const timestamp = getBinanceTimestamp();
        let queryString = "";
        if (exchangeType === "PERPETUAL") {
          // Use brokerId for futures as per docs
          queryString = `brokerId=${apiAgentCode}&timestamp=${timestamp}`;
        } else {
          // Use apiAgentCode for spot
          queryString = `apiAgentCode=${apiAgentCode}&timestamp=${timestamp}`;
        }
        const signature = await createHmacSignature(queryString, apiSecret);
        const url = `${baseUrl}${path}?${queryString}&signature=${signature}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "X-MBX-APIKEY": apiKey,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.msg || "Failed to validate API keys.");
        }

        const result: RebateValidationResult = {
          ifNewUser: data.ifNewUser,
          rebateWorking: data.rebateWorking,
          isQualified: data.ifNewUser && data.rebateWorking,
        };

        setValidationResult(result);
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred.",
        );
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { isLoading, error, validationResult, validateKeys, resetValidator };
}
